// React
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// VDS-REACT
import TabList from '@visa/vds-react/tab-list-styled';

// CYBS-UI
import CybsDivider from '@cybs-ui/components/lib/divider';
import CybsTabItem from '@cybs-ui/components/lib/tabItem';

import styles from './component.styles';
import { kebabCase } from '../../utils/StringUtils';

const CybsTab = ({
  dataTest,
  headingLevel,
  isUnderlined,
  onSelectTab,
  orientation,
  selectedTab,
  role,
  tabs,
  tag,
  ...props
}) => {
  const [selected, setSelectedTab] = useState(selectedTab);
  const panelIds = [];
  const panelRefs = [];
  useEffect(() => {
    setSelectedTab(selectedTab);
  }, [selectedTab]);
  return (
    <div
      data-test={`${dataTest}-cybsTab`}
      css={theme => styles({ isUnderlined, theme }).base}
    >
      <TabList
        selectedTab={selectedTab}
        orientation={orientation}
        role={role}
        {...props}
      >
        {tabs &&
          tabs.map((tab, index) => {
            let panelId;

            if (tab.children) {
              panelRefs.push(tab.children);
              panelId = `panel-${tab.label}`;
              panelIds.push(panelId);
            }

            return React.cloneElement(
              <CybsTabItem
                headingLevel={headingLevel}
                href={tab.href}
                i18n={{ label: tab.label }}
                key={`tab-${kebabCase(tab.label)}`}
                dataTest={dataTest}
              />,
              {
                dataTest: `${dataTest}-${index}`,
                disabled: tab.disabled,
                iconClass: tab.iconClass,
                isSelected: parseInt(selected, 10) === index,
                onClick: (e, i) => {
                  setSelectedTab(selectedTab === undefined ? i : selectedTab);
                  if (typeof onSelectTab === 'function') {
                    onSelectTab(e, i);
                  }
                },
                tag,
                ...tab.props
              }
            );
          })}
      </TabList>
      {isUnderlined && <CybsDivider style={{ margin: 0 }} />}
      <div>
        {panelRefs &&
          panelRefs.map(
            (panel, index) =>
              React.isValidElement(panel) &&
              React.cloneElement(panel, {
                key: `tablist-${panelIds[index]}`,
                style: {
                  display: parseInt(selected, 10) === index ? 'block' : 'none'
                }
              })
          )}
      </div>
    </div>
  );
};

CybsTab.propTypes = {
  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * ARIA heading level for the label of the selected TabItem.
   */
  headingLevel: PropTypes.number,

  /**
   * If `true`, tabs has a divider underneath and a tab panel content.
   */
  isUnderlined: PropTypes.bool,

  /**
   * A11y - Role for the tablist
   */
  role: PropTypes.string,

  /**
   * Event fired when a tab is clicked
   */
  onSelectTab: PropTypes.func,

  /**
   * Orientation
   */
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),

  /**
   * Selected Tab Index
   */
  selectedTab: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * List of Tabs
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
      href: PropTypes.string,
      disabled: PropTypes.bool,
      i18n: PropTypes.shape({
        label: PropTypes.string
      }),
      iconClass: PropTypes.string,
      notification: PropTypes.bool,
      notificationColor: PropTypes.string
    })
  ).isRequired,

  /**
   * Type element for the tabs.
   */
  tag: PropTypes.oneOf(['a', 'button'])
};

CybsTab.defaultProps = {
  headingLevel: undefined,
  isUnderlined: false,
  orientation: 'horizontal',
  selectedTab: undefined,
  onSelectTab: () => {},
  role: 'tablist',
  tag: 'button'
};

CybsTab.displayName = 'CybsTab';

export default CybsTab;
